import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, Loader2, Lock, ArrowRight } from 'lucide-react';
import AuthShell from '../components/AuthShell';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';

const FEATURES = [
  'Every CMS-certified SNF: quality, staffing, ownership, fines',
  'Hospital network: who refers to you, discharges by diagnosis',
  'Talking points for your next hospital CFO meeting',
  'Readmission penalties, Medicare quality bonus & daily staffing',
  'Physician alignment, Home Health & Hospice intelligence',
  'Unlimited CSV exports + Ask AI assistant',
];

export default function PricingPage() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [email, setEmail] = useState(params.get('email') || user?.email || '');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  async function startCheckout(e) {
    e.preventDefault();
    setLoading(true); setErr(null);
    try {
      const { checkout_url } = await api.subscribe(email);
      window.location.href = checkout_url;
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Could not start checkout. Please try again.');
      setLoading(false);
    }
  }

  return (
    <AuthShell compactMobile title="Piece of Cake LTC — Pro" subtitle="Full access to every module. Cancel anytime.">
      <div className="flex items-baseline gap-2" data-testid="pricing-price">
        <span className="font-display text-5xl text-[#1A2622] tabular-nums">$49.99</span>
        <span className="text-[14px] text-[#7A857F]">/ month</span>
      </div>
      <ul className="mt-5 space-y-2">
        {FEATURES.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-[13px] text-[#3A4A44]">
            <Check size={15} className="text-[#4A7C5E] shrink-0 mt-0.5" /><span>{f}</span>
          </li>
        ))}
      </ul>
      <form onSubmit={startCheckout} className="mt-6 space-y-3" data-testid="pricing-form">
        <label className="block">
          <span className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-[#3A4A44]">Your email</span>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com" data-testid="pricing-email"
            className="mt-1.5 w-full h-11 px-3.5 rounded-md bg-white border border-[#E5DCC4] text-[14px] text-[#1A2622] placeholder:text-[#A8B0AA] focus:border-[#4A7C5E] outline-none transition-colors" />
        </label>
        <button type="submit" disabled={loading} data-testid="pricing-checkout-btn"
          className="w-full h-12 rounded-md bg-[#1A2622] text-[#FBF6EC] text-[14px] font-medium hover:bg-[#2D5F4F] disabled:opacity-60 inline-flex items-center justify-center gap-2 transition-colors">
          {loading ? <><Loader2 size={16} className="animate-spin" /> Redirecting to Stripe…</> : <>Subscribe — $49.99/mo <ArrowRight size={14} /></>}
        </button>
        {err && <div className="text-[12.5px] text-[#A63A3A]" data-testid="pricing-error">{err}</div>}
      </form>
      <p className="mt-4 flex items-start gap-1.5 text-[11.5px] text-[#7A857F] leading-relaxed">
        <Lock size={12} className="mt-0.5 shrink-0" />
        Secure payment by Stripe. Your 4-digit access code appears right after checkout — use it with this email to sign in.
      </p>
      <p className="mt-4 text-[12.5px] text-[#5A6660]">
        Already subscribed? <Link to="/login" className="text-[#4A7C5E] font-medium underline underline-offset-2" data-testid="pricing-signin-link">Sign in</Link>
      </p>
    </AuthShell>
  );
}
